import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { format, parse } from 'date-fns';
import { find, isEmpty } from 'lodash-es';
import { Subject, delay, finalize, takeUntil } from 'rxjs';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktFormComponent } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';

import { DocumentCreateModel, DocumentDetails, DocumentUpdateModel, FormModel } from '@shared/models';
import { DocumentService, NotificationService } from '@shared/services';

import { documentFields } from './document-form-fields';
import { FileUploadComponent } from '../../file-upload/file-upload.component';
import { DocumentType, businessCustomerDocumentTypes, individualCustomerDocumentTypes } from '../document-type-selection/document-types';

@Component({
  selector: 'app-document-create-form',
  templateUrl: 'document-create-form.component.html',
  styleUrls: ['document-create-form.component.scss'],
  imports: [RktButtonDirective, FormsModule, ReactiveFormsModule, RktFormComponent, FileUploadComponent, RktIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class DocumentCreateFormComponent implements AfterViewChecked, OnDestroy {
  formModel: FormModel<DocumentCreateModel> = {};

  documentForm = new FormGroup({});

  documentFields = documentFields;

  documentUploadData?: File | null;

  file?: string | ArrayBuffer | null;

  backDocumentUploadData?: File | null;

  backFile?: string | ArrayBuffer | null;

  isBackFileUploaded = false;

  documentType!: string;

  accountHolderType?: DocumentCreateModel['accountHolderType'];

  isEdit = false;

  editData?: DocumentDetails;

  isLoading = false;

  get documentTypeData(): DocumentType {
    if (this.isEdit) {
      return (
        find([...individualCustomerDocumentTypes, ...businessCustomerDocumentTypes], (type) => type.value === this.documentType) ?? {
          icon: '',
          label: '',
          value: this.documentType,
        }
      );
    }

    switch (this.accountHolderType) {
      case 'Individual':
      case 'BusinessBeneficiary':
        return find(individualCustomerDocumentTypes, (type) => type.value === this.documentType)!;

      case 'Business':
        return find(businessCustomerDocumentTypes, (type) => type.value === this.documentType)!;

      default:
        return { icon: '', label: '', value: this.documentType };
    }
  }

  get isTwoSided(): boolean {
    return !!this.documentTypeData?.twoSided;
  }

  // Front is satisfied by a freshly picked file or, on edit, the already-uploaded file.
  // Back additionally required for two-sided documents.
  get isUploadReady(): boolean {
    const frontReady = this.formModel.isFileUploaded || !!this.documentUploadData;
    const backReady = this.isBackFileUploaded || !!this.backDocumentUploadData;
    return frontReady && (!this.isTwoSided || backReady);
  }

  private destroy$ = new Subject<void>();

  constructor(
    public ref: ChangeDetectorRef,
    public activeModal: ActiveModal,
    private documentService: DocumentService,
    private notificationService: NotificationService,
  ) {}

  public ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  public ngOnDestroy(): void {
    this.documentForm.markAsUntouched();
    this.destroy$.next();
    this.destroy$.complete();
  }

  modalInitData({ documentType, accountHolderType, editData }: DocumentCreateModel) {
    if (editData) {
      this.documentType = editData.documentType;
      this.isEdit = true;

      this.editData = editData;

      this.formModel = {
        ...this.formModel,
        documentType: editData.documentType,
        description: editData.description,
        expiryDate: editData.expiryDate,
        issuingDate: editData.issuingDate,
        issuingCountryCode: editData.issuingCountryCode,
        issuingStateCode: editData.issuingStateCode,
        name: editData.name,
        number: editData.number,
        isFileUploaded: !isEmpty(editData.url?.value),
        accountHolderType,
      };

      this.isBackFileUploaded = !isEmpty(editData.backExtension);
    } else {
      this.documentType = documentType;
      this.accountHolderType = accountHolderType;
      this.formModel.documentType = documentType;
      this.formModel.accountHolderType = accountHolderType;
    }
  }

  onFileUploaded(file: File | null): void {
    if (!file && this.formModel.isFileUploaded) {
      this.formModel = {
        ...this.formModel,
        isFileUploaded: false,
      };
    }
    if (!file) {
      this.documentUploadData = null;
    } else {
      this.documentUploadData = file;
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent) => {
        const fr = e.currentTarget as FileReader;
        this.file = fr.result;
      };
      reader.readAsArrayBuffer(file);
    }
  }

  onBackFileUploaded(file: File | null): void {
    if (!file && this.isBackFileUploaded) {
      this.isBackFileUploaded = false;
    }
    if (!file) {
      this.backDocumentUploadData = null;
      this.backFile = null;
    } else {
      this.backDocumentUploadData = file;
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent) => {
        const fr = e.currentTarget as FileReader;
        this.backFile = fr.result;
      };
      reader.readAsArrayBuffer(file);
    }
  }

  onSubmit() {
    if (this.documentForm.invalid || !this.isUploadReady) {
      return;
    }

    const { description, name, number, issuingDate, expiryDate, issuingCountryCode, issuingStateCode } = this.formModel;

    const matchExtension = (fileName?: string) => fileName?.match(/^.*\.(?<extension>[a-z]+)$/i)?.groups?.extension.toUpperCase();

    const documentData: Partial<DocumentUpdateModel> = {
      name,
      number,
      issuingCountryCode,
      issuingStateCode,
      description,
      extension: matchExtension(this.documentUploadData?.name),
      contentLength: this.documentUploadData?.size,
      issuingDate: issuingDate ? format(parse(issuingDate, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd') : undefined,
      expiryDate: expiryDate ? format(parse(expiryDate, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd') : undefined,
      documentType: this.documentType,
      file: this.file,
      action: 'SAVE',
    };

    if (this.isTwoSided) {
      // Send back metadata even when re-using the existing back image on edit, so the backend's
      // both-required validation passes; upload only happens when a new back file was picked.
      documentData.backFile = this.backFile;
      documentData.backExtension = matchExtension(this.backDocumentUploadData?.name) ?? this.editData?.backExtension;
      documentData.backContentLength = this.backDocumentUploadData?.size ?? this.editData?.backContentLength;
    }

    if (this.isEdit) {
      this.isLoading = true;
      this.documentService
        .updateDocument(this.editData!.id, documentData)
        .pipe(
          delay(this.file ? 3000 : 0),
          finalize(() => (this.isLoading = false)),
          takeUntil(this.destroy$),
        )
        .subscribe({
          next: () => {
            this.notificationService.displaySuccess(`Document updated successfully.`, 'Success');
            this.activeModal.close(true);
          },
        });
    } else {
      this.activeModal.close(documentData);
    }
  }
}
